import axios from 'axios';

const API_BASE = process.env.VUE_APP_API_BASE || '/api';
const APP_BASE_PATH = process.env.BASE_URL || '/';

const instance = axios.create({
  baseURL: API_BASE,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json'
  }
});

// 请求拦截器
instance.interceptors.request.use(
  config => {
    const token = localStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// 响应拦截器
instance.interceptors.response.use(
  response => {
    return response.data;
  },
  error => {
    console.error('API请求错误:', error);
    
    if (error.response) {
      const { status, data } = error.response;
      const requestUrl = String(error.config?.url || '')
      let message = '请求失败';

      if (data && data.crashed) {
        localStorage.removeItem('token')
        localStorage.removeItem('userInfo')
        const normalizedBase = APP_BASE_PATH.endsWith('/') ? APP_BASE_PATH : `${APP_BASE_PATH}/`
        if (!String(window.location.hash || '').includes('/system-crashed')) {
          window.location.href = `${normalizedBase}#/system-crashed`
        }
        return Promise.reject({
          status,
          message: data.message || '系统崩溃啦',
          crashed: true,
          data
        });
      }
      
      if (status === 401) {
        if (requestUrl.includes('/auth/login')) {
          return Promise.reject({
            status,
            message: (data && (data.error || data.message)) || '账号或密码错误',
            data
          });
        }
        localStorage.removeItem('token')
        localStorage.removeItem('userInfo')
        const normalizedBase = APP_BASE_PATH.endsWith('/') ? APP_BASE_PATH : `${APP_BASE_PATH}/`
        window.location.href = `${normalizedBase}#/login`
        return Promise.reject({
          status,
          message: '登录已过期，请重新登录',
          data
        });
      }
      
      if (data && (data.error || data.message)) {
        message = data.error || data.message;
      }
      
      return Promise.reject({
        status,
        message,
        data
      });
    } else if (error.request) {
      return Promise.reject({
        status: 0,
        message: '网络连接失败'
      });
    } else {
      return Promise.reject({
        status: -1,
        message: error.message
      });
    }
  }
);

// API 方法
export const stockApi = {
  getLoginPublicKey() {
    return instance.get('/auth/public-key');
  },

  login(payload) {
    return instance.post('/auth/login', payload);
  },

  // 竞价分析汇总（超预期个股 + 概念统计；conceptSource 同首板预选：1 同花顺 2 开盘啦 3 叠加）
  getBiddingSummary(date, conceptSource = 3) {
    return instance.post('/bidding/summary', { date, conceptSource });
  },

  // 获取涨停股票（含 MA/筹码 enrichment，偶发 >30s）
  getLimitUpStocks(date) {
    return instance.post('/gap-up/limit-up-stocks', { date }, { timeout: 90000 });
  },

  // 搜索股票
  searchStocks(keyword) {
    return instance.post('/stocks/search', { keyword });
  },

  searchConcepts(keyword, limit = 20) {
    return instance.post('/concepts/search', { keyword, limit });
  },

  // 搜索涨幅TOP股票
  searchTopStocks(date) {
    return instance.post('/gap-up/top-stocks-10d', { date });
  },

  // 概念匹配 - 近20交易日涨幅TOP30
  searchTopStocks20d(date) {
    return instance.post('/concept-match/top-stocks-20d', { date });
  },

  // 概念匹配分析
  analyzeConceptMatch(sourceCode, targetCodes = [], conceptSource = 3) {
    return instance.post('/concept-match/analyze', {
      sourceCode,
      targetCodes,
      conceptSource
    });
  },

  // 概念匹配：10/15/20 日 TOP15 三栏
  analyzeConceptMatchWindows(date, sourceCode, conceptSource = 3) {
    return instance.post('/concept-match/analyze-windows', {
      date,
      sourceCode,
      conceptSource
    });
  },

  // 首板预选分析（maxPrice：现价上限元；min/maxMarketCapYi：流通市值下限/上限亿；resultLimit：返回条数）
  analyzeFirstBoardPreselection(
    leaderCode,
    selectedConcepts = [],
    conceptSource = 3,
    {
      maxPrice = 30,
      minMarketCapYi = 0,
      maxMarketCapYi = 60,
      resultLimit = 50,
      customConcepts = [],
      refDate = '',
      onlyChiNext = false,
      attrMain = '',
      attrMain2 = '',
      attrMain3 = '',
      attrSub1 = '',
      attrSub2 = '',
      attrSub3 = '',
      leaderCodes = [],
      mode = '',
      maxMidChipPct = null,
      filterMa250 = false,
      filterSlope = false,
      filterDistToYearHigh = false,
      slopeMinPct = null,
      distToYearHighMax = null
    } = {}
  ) {
    return instance.post(
      '/first-board-preselection/analyze',
      {
        leaderCode,
        leaderCodes,
        selectedConcepts,
        customConcepts,
        conceptSource,
        maxPrice,
        minMarketCapYi,
        maxMarketCapYi,
        resultLimit,
        refDate,
        onlyChiNext: !!onlyChiNext,
        attrMain,
        attrMain2,
        attrMain3,
        attrSub1,
        attrSub2,
        attrSub3,
        mode,
        maxMidChipPct,
        filterMa250: !!filterMa250,
        filterSlope: !!filterSlope,
        filterDistToYearHigh: !!filterDistToYearHigh,
        slopeMinPct,
        distToYearHighMax
      },
      { timeout: 300000 }
    );
  },

  /** 龙头：参考日近 15 交易日首次涨停日的自由流通市值（亿） */
  getFirstBoardLeaderFreeFloat(leaderCode, refDate = '') {
    return instance.post('/first-board-preselection/leader-free-float', {
      leaderCode,
      refDate: refDate || ''
    });
  },

  syncFirstBoardToThs(leaderName, codes, selectedConcepts = [], mode = '') {
    return instance.post(
      '/first-board-preselection/sync-ths',
      { leaderName, codes, selectedConcepts, mode },
      { timeout: 300000 }
    );
  },

  /** 切换页：王国龙头 + 今日≥4板 → 同花顺分组「所有龙」 */
  syncAllDragonsToThs(codes) {
    return instance.post(
      '/gap-up/sync-all-dragons-ths',
      { codes },
      { timeout: 300000 }
    );
  },

  /** 切换页当前列表 → 同花顺分组「切换MMDD」 */
  syncGapBoardToThs(tradeDate, codes) {
    return instance.post(
      '/gap-up/sync-ths',
      { tradeDate, codes },
      { timeout: 300000 }
    );
  },

  /** 题材传播：首板创业、首板、老臣、接力 TOP，最后空分组。空池不写。 */
  syncThemePropToThs(leaderName, relayCodes, veteranCodes, leaderCode, kingdomName, firstBoardCodes, firstBoardChiCodes, dragonCodes) {
    return instance.post(
      '/theme-prop/sync-ths',
      { leaderName, relayCodes, veteranCodes, leaderCode, kingdomName, firstBoardCodes, firstBoardChiCodes, dragonCodes },
      { timeout: 300000 }
    );
  },

  /** 首板预选旁：今日首板预选池 → 前两字一首板，接力池 → 前两字一接力TOP */
  syncThemePropBoardPools(leaderName, leaderCode, firstBoardCodes, relayCodes, firstBoardChiCodes) {
    return instance.post(
      '/theme-prop/sync-board-pools',
      { leaderName, leaderCode, firstBoardCodes, relayCodes, firstBoardChiCodes },
      { timeout: 300000 }
    );
  },

  // 补涨分析 - 按日期抓取并入库涨停列表
  syncGapUpDayData(tradeDate) {
    // 含当日 StockDaily 全市场同步，超时放宽
    return instance.post('/tasks/sync-daily-core', { tradeDate }, { timeout: 300000 });
  },

  /** 当日 DailyData（含 greedyNodeScore） */
  fetchDailyData(tradeDate) {
    return instance.get(`/daily/${encodeURIComponent(tradeDate)}`);
  },

  /** 只读：监控池个股区间前复权收盘 */
  fetchStockDailyCloses({ codes, startDate, endDate }) {
    return instance.post('/stocks/daily-closes', { codes, startDate, endDate });
  },

  /** 全 A 近 10 日涨幅每日前 5 */
  fetchAllABurstTop({ endDate, window, lookback }) {
    return instance.post('/stocks/burst-top', { endDate, window, lookback }, { timeout: 120000 });
  },

  /** 周期图：问财、开盘啦、地名合并后的概念 */
  fetchConceptNames({ codes }) {
    return instance.post('/stocks/concept-names', { codes }, { timeout: 30000 });
  },

  getCycleLineConcepts() {
    return instance.get('/cycle/line-concepts');
  },

  saveCycleLineConcept({ code, concepts, concept, match, nameKeys, kingdomTitle }) {
    return instance.put('/cycle/line-concepts', { code, concepts, concept, match, nameKeys, kingdomTitle });
  },

  saveCycleLineHidden({ code, hidden }) {
    return instance.put('/cycle/line-hidden', { code, hidden });
  },

  saveCycleLineMembers({ code, tradeDate, members, removed }) {
    return instance.put('/cycle/line-members', { code, tradeDate, members, removed });
  },

  /** 连板高度线上的 V2A 爆量点 */
  fetchV2AVolumeMarks({ stocks, through }) {
    return instance.post('/cycle/v2a-volume-marks', { stocks, through }, { timeout: 60000 });
  },

  /** 贪心 V5F 节点目录 */
  fetchGreedyNodeCatalog() {
    return instance.get('/greedy-node-catalog');
  },

  /** 补涨分析：读取某日逻辑匹配 */
  fetchGapUpLogicMatch(tradeDate) {
    return instance.get(`/gap-up/logic-match/${encodeURIComponent(tradeDate)}`);
  },

  /** 补涨分析：保存单条逻辑匹配 */
  saveGapUpLogicMatch(tradeDate, code, entry) {
    return instance.put('/gap-up/logic-match', { tradeDate, code, entry });
  },

  // 竞价分析 - 获取数据
  getBiddingData(date) {
    return instance.post('/getBiddingData', { date });
  },

  // 竞价分析 - 获取个股关联打分列表
  getBiddingStockList(code, date, conceptSource = 3) {
    return instance.post('/bidding/stock-list', { code, date, conceptSource });
  },

  // 竞价分析 - 按日期抓取并入库竞价列表
  syncBiddingDayData(tradeDate) {
    return instance.post('/tasks/sync-auction', { tradeDate, limit: 500 }, { timeout: 180000 });
  },

  // 龙池 - 获取列表
  getDragonPoolList() {
    return instance.get('/getDragonPoolList');
  },

  // 龙池 - 添加股票
  addDragonPoolStock(data) {
    return instance.post('/addDragonPoolStock', data);
  },

  // 龙池 - 删除股票
  deleteDragonPoolStock(code) {
    return instance.post('/deleteDragonPoolStock', { code });
  },

  getStocksFreeFloatMarketCap(codes) {
    return instance.post('/stocks/free-float-market-cap', { codes });
  },

  getTrendSubPlates({ dStart, dEnd, parentPlateId }) {
    return instance.post('/trend-analysis/sub-plates', { dStart, dEnd, parentPlateId }, { timeout: 120000 });
  },

  getTrendIntervalStocks({ dStart, dEnd, subPlateId }) {
    return instance.post('/trend-analysis/interval-stocks', { dStart, dEnd, subPlateId }, { timeout: 120000 });
  },

  getTrendDailyDetails(payload) {
    return instance.post('/trend-analysis/daily-details', payload, { timeout: 180000 });
  },

  getTrendDailyDetailsBatch(payload) {
    return instance.post('/trend-analysis/daily-details-batch', payload, { timeout: 300000 });
  },

  exportTrendAnalysis(payload) {
    return instance.post('/trend-analysis/export', payload, {
      responseType: 'blob',
      timeout: 300000
    });
  },

  fetchReviewSeries({ startDate, endDate }) {
    return instance.post('/review-notes/series', { startDate, endDate });
  },

  syncReviewRange({ startDate, endDate }) {
    return instance.post('/review-notes/sync-range', { startDate, endDate }, { timeout: 300000 });
  },

  syncReviewMa250({ startDate, endDate }) {
    return instance.post('/review-notes/sync-ma250', { startDate, endDate }, { timeout: 300000 });
  },

  saveReviewDayNote(payload) {
    return instance.put('/review-notes/day-note', payload);
  },

  getMarketNodeFactors(tradeDate) {
    return instance.get('/review-notes/market-node-factors', { params: { tradeDate } });
  },

  saveMarketNodeFactors({ tradeDate, fields }) {
    return instance.put('/review-notes/market-node-factors', { tradeDate, fields });
  },

  saveReviewPickedStocks({ tradeDate, owner, pickedStocks }) {
    return instance.put('/review-notes/picked-stocks', { tradeDate, owner, pickedStocks });
  },

  savePreFirstBoardStocks({ tradeDate, owner, pickedStocks }) {
    return instance.put('/review-notes/pre-first-board-stocks', { tradeDate, owner, pickedStocks });
  },

  fetchReviewLadder({ tradeDate, themeCode }) {
    return instance.post('/review-notes/limit-up-ladder', { tradeDate, themeCode });
  },

  fetchRecordLibrary() {
    return instance.get('/review-notes/record-library');
  },

  addRecordLibrary(payload) {
    return instance.post('/review-notes/record-library', payload || {});
  },

  addAuctionTopLibrary(payload) {
    return instance.post('/auction-top/library', payload || {});
  },

  fetchAuctionTopLibrary(tradeDate) {
    return instance.get('/auction-top/library', { params: { tradeDate } });
  },

  /** 相对竞价日取上一交易日 TOP 库 */
  fetchAuctionTopLibraryPrevOf(prevOf) {
    return instance.get('/auction-top/library', { params: { prevOf } });
  },

  queryAuctionTop(payload) {
    return instance.post('/auction-top/query', payload || {}, { timeout: 120000 });
  },

  /** 竞价 TOP 逻辑列：批量查匹配股当日竞价涨幅 */
  fetchMatchAuctionGains(tradeDate, codes) {
    return instance.post('/auction-top/match-auction-gains', {
      tradeDate,
      codes: Array.isArray(codes) ? codes : []
    });
  },

  updateRecordLibrary(id, payload) {
    return instance.put(`/review-notes/record-library/${id}`, payload);
  },

  deleteRecordLibrary(id) {
    return instance.delete(`/review-notes/record-library/${id}`);
  },

  fetchGapUpLibrary() {
    return instance.get('/review-notes/gap-up-library');
  },

  addGapUpLibrary(payload) {
    return instance.post('/review-notes/gap-up-library', payload || {}, { timeout: 120000 });
  },

  updateGapUpLibrary(id, payload) {
    return instance.put(`/review-notes/gap-up-library/${id}`, payload || {}, { timeout: 120000 });
  },

  deleteGapUpLibrary(id) {
    return instance.delete(`/review-notes/gap-up-library/${id}`);
  },

  fetchGapUpLeaderConcepts(code) {
    return instance.get('/review-notes/gap-up-library/leader-concepts', {
      params: { code }
    });
  },

  queryDataBacktest({ startDate, endDate }) {
    return instance.post(
      '/data-backtest/query',
      { startDate, endDate },
      { timeout: 300000 }
    );
  },

  queryHistoryFirstBoard({ startDate, endDate }) {
    return instance.post(
      '/history-first-board/query',
      { startDate, endDate },
      { timeout: 300000 }
    );
  },

  nextHistoryFirstBoardTradeDate(tradeDate) {
    return instance.post('/history-first-board/next-trade-date', { tradeDate });
  },

  prevHistoryFirstBoardTradeDate(tradeDate) {
    return instance.post('/history-first-board/prev-trade-date', { tradeDate });
  },

  resolveHistoryFirstBoardTradeDate(tradeDate) {
    return instance.post('/history-first-board/resolve-trade-date', { tradeDate });
  },

  /** 情绪节点分析｜接力生态联动看板曲线（只读） */
  getRelayEcologyCurve(date, window = 20) {
    return instance.get('/relay-ecology/curve', {
      params: { date, window }
    });
  },

  /** 复盘表｜与情绪节点同一窗口（只读） */
  getReviewBoard(endDate, window = 20) {
    return instance.get('/review-board', {
      params: { endDate, window }
    });
  },

  /** 情绪节点分析｜S/S+ 次日 5+ winners + NCI（V2 预计算缓存，只读） */
  getRelayEcologyNextDayStageResults(soilDate) {
    return instance.get('/relay-ecology/next-day-stage-results', {
      params: { soilDate },
      timeout: 15000
    });
  },

  getLeaderCommandCatalog() {
    return instance.get('/leader-command-center/catalog');
  },

  getLeaderCommandSnapshot({ tradeDate, scopeId, decisionTime, mode }) {
    return instance.get('/leader-command-center/snapshot', {
      params: { tradeDate, scopeId, decisionTime, mode },
      timeout: 90000
    });
  },

  getLeaderCommandStock(code, params) {
    return instance.get(`/leader-command-center/stocks/${encodeURIComponent(code)}`, {
      params,
      timeout: 60000
    });
  },

  getLeaderCommandCandidate({ tradeDate, decisionTime, mode, includeNfiAudit }) {
    return instance.get('/leader-command-center/candidate', {
      params: { tradeDate, decisionTime, mode, includeNfiAudit },
      timeout: 120000
    });
  },

  getLeaderCommandMonitoring({ tradeDate, decisionTime, mode, includeNfiAudit, manualEpisodes }) {
    const params = { tradeDate, decisionTime, mode, includeNfiAudit };
    if (manualEpisodes && manualEpisodes.length) {
      params.manualEpisodes = JSON.stringify(manualEpisodes);
    }
    return instance.get('/leader-command-center/monitoring', {
      params,
      timeout: 120000
    });
  },

  /** 王国趋势龙/连板龙点选票：今日涨幅 + 10/15/20 日总涨幅 */
  postLeaderRoleReturns({ tradeDate, codes }) {
    return instance.post('/leader-command-center/role-returns', {
      tradeDate,
      codes
    }, { timeout: 30000 });
  },

  /** 龙头传播：近 20 个交易日内首次涨停至今的问财题材涨幅 */
  postLeaderThemeReturns({ tradeDate, code, concepts }) {
    return instance.post('/leader-command-center/theme-returns', {
      tradeDate,
      code,
      concepts
    }, { timeout: 60000 });
  },

  /** 题材传播：曾≥4B 池（首板→退池/as-of；封死≥4 或 3B 次日摸板） */
  postLeaderOnce4b({
    tradeDate,
    leaderCode,
    concept,
    conceptSource,
    exitDate,
    anchorDate
  }) {
    return instance.post(
      '/leader-command-center/once-4b',
      {
        tradeDate,
        leaderCode,
        concept,
        conceptSource,
        exitDate,
        anchorDate
      },
      { timeout: 90000 }
    );
  },

  /** 题材传播：各题材曾≥4B 家数（与 once-4b 同口径） */
  postLeaderOnce4bCounts({
    tradeDate,
    leaderCode,
    concepts,
    exitDate,
    anchorDate
  }) {
    return instance.post(
      '/leader-command-center/once-4b-counts',
      {
        tradeDate,
        leaderCode,
        concepts,
        exitDate,
        anchorDate
      },
      { timeout: 90000 }
    );
  },

  /** 所有账号共用的王国 / 接力池操作记录 */
  getLccSharedState() {
    return instance.get('/leader-command-center/shared-state', { timeout: 20000 });
  },

  putLccSharedState({ entries, asOfTradeDate, baseUpdatedAt }) {
    return instance.put(
      '/leader-command-center/shared-state',
      { entries, asOfTradeDate, baseUpdatedAt },
      { timeout: 20000 }
    );
  },

  /** 主属性概念：近 10/15/20 个交易日涨幅前 10 */
  postThemeWindowReturns({ tradeDate, concepts }) {
    return instance.post(
      '/leader-command-center/theme-window-returns',
      { tradeDate, concepts },
      { timeout: 60000 }
    );
  },

  /** 手动名单：截至交易日的三连阴 / 双跌停 */
  postHeightMood({ tradeDate, codes }) {
    return instance.post(
      '/leader-command-center/height-mood',
      { tradeDate, codes },
      { timeout: 30000 }
    );
  }
};

export default stockApi;
